import { useCallback, useEffect } from 'react';

import { FormPreview } from '@/features/dashboard/components/AppForm/AppForm/components/FormPreview';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import { getFormAppDefinition } from '@/features/dashboard/components/apps';
import type { FormAppType } from '@/features/dashboard/components/apps';
import AddAppHeader from '@/features/dashboard/components/Drawer/components/AddAppHeader';
import { useSaveApp } from '@/features/dashboard/components/Drawer/components/AddAppHeader/useSaveApp';
import { DashboardDrawerLayout } from '@/features/dashboard/components/Drawer/components/DashboardDrawerLayout';
import { DrawerFooterButtons } from '@/features/dashboard/components/Drawer/components/DrawerFooterButtons';
import { useDrawerSteps } from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import Heading from '@/features/dashboard/components/Heading';

const Content = ({ type }: { type: FormAppType | undefined }) => {
  if (!type) return null;
  const { Form } = getFormAppDefinition(type);
  return <Form />;
};

// Helper component to sync form dirty state to the drawer without setting state during render
const HasChangesSync = ({
  isDirty,
  setHasChanges
}: {
  isDirty: boolean;
  setHasChanges: (v: boolean) => void;
}) => {
  useEffect(() => {
    setHasChanges(isDirty);
  }, [isDirty, setHasChanges]);
  return null;
};

export const AppForm = () => {
  const { setHasChanges, handleAttemptClose } = useDrawerSteps();
  const { form } = useAppForm();
  const { handleSave, isPending } = useSaveApp();

  const createWidget = useCallback(() => {
    const values = form.state.values;
    if (!values?.type) return;
    return getFormAppDefinition(values.type).toWidget(values);
  }, [form]);

  return (
    <DashboardDrawerLayout
      footer={
        <form.Subscribe selector={s => s.isValid}>
          {isValid => (
            <DrawerFooterButtons
              disabled={isPending || !isValid}
              onDiscard={handleAttemptClose}
              onSave={() => {
                handleSave(createWidget());
              }}
            />
          )}
        </form.Subscribe>
      }
      header={<AddAppHeader />}
      subheader={
        <form.Subscribe selector={s => s.values?.type}>
          {type => (type ? <Heading {...getFormAppDefinition(type).heading} /> : null)}
        </form.Subscribe>
      }
    >
      <form.Subscribe selector={s => s.values?.type}>
        {type => <Content type={type} />}
      </form.Subscribe>
      <div className='mt-6'>
        <FormPreview />
      </div>

      {/* Sync the drawer's hasChanges flag with the form's dirty state */}
      <form.Subscribe selector={s => s.isDirty}>
        {isDirty => (
          <HasChangesSync
            isDirty={isDirty}
            setHasChanges={setHasChanges}
          />
        )}
      </form.Subscribe>
    </DashboardDrawerLayout>
  );
};
